import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RefreshCw, Activity, Moon, CheckCircle2, AlertCircle } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { relativeDistance } from "@/lib/dates";
import {
  getSyncStatus,
  triggerIntervalsSync,
  triggerWellnessSync,
  type WellnessSyncResult,
} from "@/api/sync";
import { useState } from "react";
import type { SyncResult } from "@/api/integrations";
import { WorkoutSyncResult, workoutSyncFailureKey } from "./IntervalsCard";
import { LoadError } from "@/components/ui/LoadError";
import { invalidateSyncData } from "@/lib/syncQueries";
import { useSyncTask } from "@/lib/useSyncTask";

export function SyncStatusCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const task = useSyncTask();
  const busy = task.busy;
  const q = useQuery({
    queryKey: ["sync-status"],
    queryFn: ({ signal }) => getSyncStatus(signal),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const [workouts, setWorkouts] = useState<SyncResult | "intervals_card.sync_failed" | "intervals_card.source_restricted" | null>(null);
  const [wellness, setWellness] = useState<WellnessSyncResult | "failed" | null>(null);
  const resultMessages = <>
    {typeof workouts === "string" ? <p role="alert" className="mt-3 text-sm text-coral">{t(workouts)}</p>
      : workouts && <WorkoutSyncResult result={workouts} />}
    {wellness && (wellness !== "failed" && wellness.status === "success"
      ? <p role="status" className="mt-3 text-sm text-navy-deep">{t("sync_status.wellness_synced", { new: wellness.new, updated: wellness.updated, total: wellness.total })}</p>
      : <p role="alert" className="mt-3 text-sm text-coral">{t(wellness !== "failed" && wellness.status === "skipped" ? "sync_status.wellness_skipped" : "sync_status.wellness_failed")}</p>)}
  </>;

  const run = () => {
    task.run(async (signal, current) => {
      setWorkouts(null); setWellness(null);
      const [workoutResult, wellnessResult] = await Promise.allSettled([triggerIntervalsSync(signal), triggerWellnessSync(signal)]);
      if (!current()) return;
      setWorkouts(workoutResult.status === "fulfilled" ? workoutResult.value : workoutSyncFailureKey(workoutResult.reason));
      setWellness(wellnessResult.status === "fulfilled" ? wellnessResult.value : "failed");
      await invalidateSyncData(qc, current);
    });
  };

  if (q.isLoading) {
    return (
      <Card>
        <SectionLabel>{t("sync_status.section_label")}</SectionLabel>
        <p className="mt-3 text-[13px] text-taupe">{t("sync_status.loading")}</p>
      </Card>
    );
  }
  if (q.isError || !q.data) {
    return <Card><SectionLabel>{t("sync_status.section_label")}</SectionLabel>
      <LoadError onRetry={() => void q.refetch()} retrying={q.isFetching} />
      {resultMessages}
    </Card>;
  }
  const d = q.data;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("sync_status.section_label")}</SectionLabel>
        {d.connected ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
            <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
            {t("sync_status.connected")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-coral-dark">
            <AlertCircle className="h-3 w-3" strokeWidth={2} />
            {t("sync_status.disconnected")}
          </span>
        )}
      </div>

      {!d.connected ? (
        <p className="mt-3 text-[13px] text-taupe">
          {t("sync_status.connect_hint")}
        </p>
      ) : (
        <>
          <div className="mt-3 space-y-2 text-[12px]">
            <Row
              icon={<RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} />}
              label={t("sync_status.next_run")}
              value={
                d.hourly_job.next_run_at
                  ? relativeDistance(d.hourly_job.next_run_at)
                  : t("sync_status.disabled")
              }
            />
            <Row
              icon={<Activity className="h-3.5 w-3.5" strokeWidth={1.8} />}
              label={t("sync_status.last_workout")}
              value={
                d.last_workout
                  ? `${d.last_workout.date}${d.last_workout.source_unavailable ? ` — ${t("workout_detail.source_unavailable")} · Strava · Intervals.icu` : d.last_workout.title_hint ? ` — ${d.last_workout.title_hint}` : ""}`
                  : t("sync_status.no_data")
              }
            />
            <Row
              icon={<Moon className="h-3.5 w-3.5" strokeWidth={1.8} />}
              label={t("sync_status.last_wellness")}
              value={
                d.last_wellness && d.last_wellness.synced_at
                  ? t("sync_status.wellness_pull_inline", {
                      date: d.last_wellness.date,
                      distance: relativeDistance(d.last_wellness.synced_at),
                    })
                  : t("sync_status.no_data")
              }
            />
          </div>
          <div className="mt-4">
            <Button
              onClick={run}
              disabled={busy}
              variant="secondary"
              className="inline-flex items-center gap-1.5"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`}
                strokeWidth={2}
              />
              {busy ? t("sync_status.syncing") : t("sync_status.sync_now")}
            </Button>
          </div>
        </>
      )}
      {resultMessages}
    </Card>
  );
}

function Row({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-taupe">{icon}</span>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-taupe">
          {label}
        </div>
        <div className="text-navy-deep">{value}</div>
      </div>
    </div>
  );
}
